import { createSignal, onMount } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { achPaymentProcessingSettingsRepository } from '~/repositories/settings/achPaymentProcessingSettingsRepository';
import type {
  ACHPaymentProcessingSettings,
  ACHPaymentProcessingSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

export const [ACHPaymentProcessingSettingsProvider, useACHPaymentProcessingSettings] = createMagicDoorContext(
  'ACHPaymentProcessingSettings',
  () => {
    const repo = new achPaymentProcessingSettingsRepository();
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const [allACHPaymentProcessingSettings, setAllACHPaymentProcessingSettings] = createSignal<
      ACHPaymentProcessingSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    >(undefined);

    const fetchAllSettings = async () => {
      setIsLoading(true);
      try {
        const result = await repo.getAllAchPaymentProcessingSettings();
        setAllACHPaymentProcessingSettings(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const updateACHPaymentProcessingSettingsByLevel = async (
      level: string,
      levelId: string,
      payload: ACHPaymentProcessingSettings
    ): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.updateAchPaymentProcessingSettingsByLevel(level, levelId, payload);
        await fetchAllSettings();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const deleteACHPaymentProcessingSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.deleteAchPaymentProcessingSettingsByLevel(level, levelId);
        await fetchAllSettings();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const updateACHPaymentProcessingSettings = async (payload: ACHPaymentProcessingSettings): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.changeAchPaymentProcessingSettings(payload);
        await fetchAllSettings();
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    onMount(() => {
      fetchAllSettings();
    });

    return {
      isLoading,
      error,
      allACHPaymentProcessingSettings,
      updateACHPaymentProcessingSettings,
      updateACHPaymentProcessingSettingsByLevel,
      deleteACHPaymentProcessingSettingsByLevel,
    };
  }
);
